import {
  PaidService,
  PaidServiceTargetTypeEnum,
  PaymentMethod,
  PaymentMethodEnum
} from '@/__generated_graphql__/graphql';
import { PaidServiceOption } from '@/__generated_graphql__/types';
import {
  DEFAULT_PRIORITY,
  WAY_TO_METHODS
} from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.constants';
import {
  ActivePaidService,
  MappedServiceType,
  PLAN_VARIANT,
  PLAN_VARIANT_THEME_KEYS,
  PlanCardStyle,
  PlanVariantThemeKey,
  ViewMethod
} from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.types';
import { MIN_TOPUP_CARD_AMOUNT, PaymentWayKey } from '@/constants/common';
import { CardAd } from '@/types';

export const mapAdToPaidServiceTarget = (
  ad?: CardAd
): {
  targetType: PaidServiceTargetTypeEnum;
  targetId?: string;
} => {
  // eslint-disable-next-line no-underscore-dangle
  if (!ad || !('__typename' in ad) || !ad.__typename) {
    return { targetType: PaidServiceTargetTypeEnum.User, targetId: undefined };
  }

  const typeMap: Record<string, PaidServiceTargetTypeEnum> = {
    RESUME: PaidServiceTargetTypeEnum.Resume,
    VACANCY: PaidServiceTargetTypeEnum.Vacancy
  };

  // eslint-disable-next-line no-underscore-dangle
  const normalizedType = ad.__typename.toUpperCase();
  const resolvedType =
    typeMap[normalizedType] ?? PaidServiceTargetTypeEnum.User;

  return {
    targetType: resolvedType,
    targetId:
      resolvedType !== PaidServiceTargetTypeEnum.User
        ? String(ad.id)
        : undefined
  };
};

export const pickPaymentMethod = (
  currentService: PaidService | undefined,
  way: PaymentWayKey
): PaymentMethodEnum | null => {
  if (!currentService) return null;

  const availableMethods = new Set(
    (currentService.paymentMethods ?? []).map(
      method => method.id as PaymentMethodEnum
    )
  );

  return (
    WAY_TO_METHODS[way].find(method => availableMethods.has(method)) ?? null
  );
};

export const getCurrentService = (
  paidServices: PaidService[],
  serviceType: MappedServiceType
) => {
  if (!paidServices?.length) return undefined;

  return (
    paidServices.find(service => service.id === serviceType) ?? paidServices[0]
  );
};

export const mapPaymentMethods = (
  paymentMethods?: Array<
    Pick<PaymentMethod, 'id' | 'name' | 'balance' | 'isAppleGooglePayEnabled'>
  > | null
): ViewMethod[] => {
  const paymentList = paymentMethods ?? [];
  return paymentList.map(method => ({
    id: method.id as PaymentMethodEnum,
    name: method.name,
    balance: method.balance ?? 0,
    isAppleGooglePayEnabled: method.isAppleGooglePayEnabled ?? false
  }));
};

export const detectDefaultPaymentMethod = (
  methods: ViewMethod[]
): PaymentMethodEnum | undefined => {
  if (!methods?.length) return undefined;

  const available = new Set(methods.map(method => method.id));
  return (
    DEFAULT_PRIORITY.find(method => available.has(method)) ?? methods[0]?.id
  );
};

export const mapPaidServicesWithOptions = (
  services: PaidService[]
): PaidService[] =>
  services.map(service => ({
    ...service,
    options: service.options.map(option => ({ ...option }))
  }));

export const planCardStyleMap: Record<PlanVariantThemeKey, PlanCardStyle> = {
  [PLAN_VARIANT_THEME_KEYS.ACTIVE_LIGHT]: {
    color: 'var(--textAndLabels-main)'
  },
  [PLAN_VARIANT_THEME_KEYS.INACTIVE_LIGHT]: {
    color: 'var(--textAndLabels-main)'
  },
  [PLAN_VARIANT_THEME_KEYS.ACTIVE_DARK]: {
    color: 'var(--bg-main)'
  },
  [PLAN_VARIANT_THEME_KEYS.INACTIVE_DARK]: {
    color: 'var(--textAndLabels-main)'
  }
};

const fallbackStyle: PlanCardStyle = {
  color: 'var(--textAndLabels-main)'
};

export const getPlanCardStyle = (
  isActive: boolean,
  isDark: boolean
): PlanCardStyle => {
  const variant = isActive ? PLAN_VARIANT.active : PLAN_VARIANT.inactive;

  const themeMode = isDark ? 'dark' : 'light';

  const key = `${variant}_${themeMode}` as PlanVariantThemeKey;
  return planCardStyleMap[key] ?? fallbackStyle;
};

export const getPaidService = (
  chip: PaidServiceOption | null,
  value: number
): ActivePaidService => {
  if (!chip?.id) return null;
  const { id, price } = chip;

  if (!price)
    return {
      serviceId: id,
      price: value
    };

  return id;
};

export const isValidDepositRange = (
  extractedPrice: number | null,
  maxValue: number
): boolean =>
  typeof extractedPrice === 'number' &&
  extractedPrice >= MIN_TOPUP_CARD_AMOUNT &&
  extractedPrice <= maxValue;

export const formatNumberWithSpaces = (value: number): string =>
  value === 0 ? '0' : value.toLocaleString('uk-UA');
